import React from "react";
import { Card } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { SEOHead } from './SEOHead';
import { PageFaq } from './PageAeo';
import { useSiteSettings, getPageSeo, buildFaqSchema, safeUrl } from '../services/SiteSettings';
import { SITE_URL } from '../data/organizationSchema.mjs';

const firstSentence = (text = '') => {
  const match = text.match(/^[\s\S]*?[.!?](\s|$)/);
  return (match ? match[0] : text).trim();
};

export function TeamPage() {
  const settings = useSiteSettings();
  const members = settings.team.members || [];
  const seo = getPageSeo(settings, '/team');

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
      { "@type": "ListItem", "position": 2, "name": "Team", "item": `${SITE_URL}/team` }
    ]
  };

  const teamSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": settings.contact.name,
    "url": `${SITE_URL}/`,
    "member": members.map((m) => {
      const image = safeUrl(m.image);
      return {
        "@type": "Person",
        "name": m.name,
        "jobTitle": m.role,
        "description": firstSentence(m.bio),
        ...(image ? { "image": image.startsWith('/') ? `${SITE_URL}${image}` : image } : {}),
        "worksFor": { "@type": "Organization", "name": settings.contact.name }
      };
    })
  };

  const faqSchema = buildFaqSchema(seo.faqs);

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={seo.title || 'Our Team'}
        description={seo.description}
        canonical={`${SITE_URL}/team`}
        keywords={seo.keywords}
        takeaways={seo.takeaways}
        structuredData={faqSchema ? [breadcrumbSchema, teamSchema, faqSchema] : [breadcrumbSchema, teamSchema]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <h1 className="mb-6">Our Team</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">{seo.description}</p>
        </header>

        <section className="mb-16 space-y-8">
          {members.map((member, index) => (
            <Card key={index} className="overflow-hidden">
              <article className="grid grid-cols-1 sm:grid-cols-4 gap-6 p-6">
                <div className="flex justify-center">
                  <ImageWithFallback
                    src={safeUrl(member.image)}
                    alt={member.name}
                    className="w-32 h-32 rounded-full object-cover"
                    fallbackSrc="/team/placeholder.jpg"
                  />
                </div>
                <div className="sm:col-span-3">
                  <h2 className="text-xl font-semibold mb-1">{member.name}</h2>
                  <p className="text-primary mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm whitespace-pre-line">{member.bio}</p>
                </div>
              </article>
            </Card>
          ))}
        </section>

        <section className="bg-primary text-primary-foreground rounded-lg p-12 text-center">
          <h2 className="mb-4">Join Our Team</h2>
          <p className="text-lg mb-6 text-primary-foreground/80 max-w-2xl mx-auto">
            We're always looking for talented, passionate individuals to join our growing team.
          </p>
          <a href="/careers" className="inline-block bg-white text-primary px-6 py-3 rounded-md hover:bg-gray-100 transition-colors">
            View Open Positions
          </a>
        </section>
      </div>

      <div className="mt-16">
        <PageFaq faqs={seo.faqs} />
      </div>
    </div>
  );
}
